import { getScrollContainer } from '@theme/scroll-container';
import { sectionRenderer } from '@theme/section-renderer';

const host = document.querySelector('tb-path-navigation');

if (host && !window.tbPathNavigationReady) {
  window.tbPathNavigationReady = true;
  const scope = host.dataset.scope;
  const storageKey = `tb:path:v1:${scope}`;
  const maxAge = 8 * 60 * 60 * 1000;
  const root = new URL(host.dataset.root, location.origin).pathname.replace(/\/$/, '');
  const nativeReplace = history.replaceState.bind(history);
  const nativePush = history.pushState.bind(history);
  const back = host.querySelector('[data-path-back]');
  const backLabel = host.querySelector('[data-path-back-label]');
  const details = host.querySelector('[data-path-history]');
  const trailList = host.querySelector('[data-path-trail]');
  const fullList = host.querySelector('[data-path-full]');
  const fallbackHref = back.getAttribute('href');
  const fallbackLabel = backLabel.textContent;
  let trail = [];
  let intent = null;
  let writing = false;
  let renderedUrl = location.href;
  let cancelRestore = () => {};
  let renderVersion = 0;
  let departureCard = null;

  function localURL(value) {
    if (typeof value !== 'string' || !value) return null;
    try {
      const url = new URL(value, location.href);
      if (url.origin !== location.origin || !['http:', 'https:'].includes(url.protocol)) return null;
      return url;
    } catch {
      return null;
    }
  }

  function pageKey(value) {
    const url = localURL(value);
    if (!url) return null;
    let path = url.pathname.replace(/\/$/, '');
    if (root && path !== root && !path.startsWith(`${root}/`)) return null;
    if (root) path = path.slice(root.length);
    if (!path) return 'index';
    const product = path.match(/^\/(?:collections\/[^/]+\/)?products\/([^/]+)$/);
    if (product) return `product:${product[1]}`;
    const collection = path.match(/^\/collections\/([^/]+)(?:\/[^/]+)?$/);
    if (collection) return `collection:${collection[1]}`;
    const article = path.match(/^\/blogs\/([^/]+)\/([^/]+)$/);
    if (article && article[2] !== 'tagged') return `article:${article[1]}/${article[2]}`;
    const blog = path.match(/^\/blogs\/([^/]+)(?:\/tagged\/[^/]+)?$/);
    if (blog) return `blog:${blog[1]}`;
    const page = path.match(/^\/(pages|policies)\/([^/]+)$/);
    if (page) return `${page[1]}:${page[2]}`;
    if (path === '/search') return `search:${(url.searchParams.get('q') || '').trim()}`;
    if (path === '/cart' || path === '/collections') return path.slice(1);
    return null;
  }

  function cleanURL(value) {
    const url = localURL(value);
    if (!url || !pageKey(url.href)) return null;
    for (const key of [...url.searchParams.keys()]) {
      if (!/^(q|sort_by|page|type|variant|selling_plan|preview_theme_id|options\[[^\]]+\]|filter\.[^\s]+)$/.test(key)) url.searchParams.delete(key);
    }
    return `${url.pathname}${url.search}${url.hash}`;
  }

  function stored() {
    try {
      const value = JSON.parse(sessionStorage.getItem(storageKey));
      return value?.scope === scope && Date.now() - value.time < maxAge ? value : null;
    } catch {
      return null;
    }
  }

  function validTrail(value) {
    if (!Array.isArray(value)) return [];
    return value.slice(-80).filter((entry) => entry && typeof entry.id === 'string' && typeof entry.title === 'string' && entry.title.length < 1000 && cleanURL(entry.url) && pageKey(entry.url) === entry.key).map((entry) => ({ ...entry, url: cleanURL(entry.url) }));
  }

  function stateTrail() {
    const value = history.state?.tbPathV1;
    return value?.scope === scope ? validTrail(value.trail) : [];
  }

  function persist() {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({ scope, time: Date.now(), trail, intent }));
    } catch {}
  }

  function stamp(state = history.state) {
    const previous = state && typeof state === 'object' ? state : {};
    try {
      nativeReplace({ ...previous, tbPathV1: { scope, trail } }, '');
    } catch {}
    persist();
  }

  function currentEntry() {
    const type = pageKey(location.href)?.split(':')[0] || host.dataset.type;
    const title = type === 'search' ? host.dataset.search.replace('[terms]', new URL(location.href).searchParams.get('q') || '') : host.dataset.title;
    return { id: crypto.randomUUID(), key: pageKey(location.href), title, type, url: cleanURL(location.href), scroll: 0, anchor: null };
  }

  function visit(previous, next) {
    const index = previous.findIndex((entry) => entry.key === next.key);
    if (index >= 0) return [...previous.slice(0, index), { ...next, id: previous[index].id }].slice(-80);
    return [...previous, next].slice(-80);
  }

  function viewportTop(container) {
    return container === document.scrollingElement || container === document.documentElement ? 0 : container.getBoundingClientRect().top;
  }

  function remember(clicked) {
    const entry = trail.at(-1);
    if (!entry) return;
    const container = getScrollContainer();
    const top = viewportTop(container);
    const cards = [...document.querySelectorAll('#MainContent [data-product-id][data-page]')];
    const clickedCard = clicked?.closest?.('[data-product-id][data-page]');
    const card = clickedCard || cards.find((node) => node.getBoundingClientRect().bottom > top + 130 && node.getBoundingClientRect().top < top + container.clientHeight);
    entry.url = cleanURL(location.href) || entry.url;
    entry.scroll = Math.max(0, container.scrollTop);
    entry.anchor = card ? { product: card.dataset.productId, offset: card.getBoundingClientRect().top - top } : null;
    if (card && ['collection', 'search'].includes(entry.type)) {
      const url = localURL(entry.url);
      url.searchParams.set('page', card.dataset.page);
      url.hash = '';
      entry.url = cleanURL(url.href);
    }
  }

  function link(entry, index) {
    const anchor = document.createElement('a');
    anchor.href = entry.url;
    anchor.textContent = entry.title;
    anchor.title = entry.title;
    anchor.dataset.pathIndex = String(index);
    return anchor;
  }

  function render() {
    const hasPrevious = trail.length > 1;
    host.toggleAttribute('data-has-trail', hasPrevious);
    details.hidden = !hasPrevious;
    details.open = false;
    trailList.replaceChildren();
    fullList.replaceChildren();
    const visible = Math.max(2, Math.min(5, Number(host.dataset.visible) || 3));
    details.toggleAttribute('data-overflow', trail.length > visible);
    trail.forEach((entry, index) => {
      const isCurrent = index === trail.length - 1;
      const item = document.createElement('li');
      const content = isCurrent ? document.createElement('span') : link(entry, index);
      if (isCurrent) {
        content.textContent = entry.title;
        content.title = entry.title;
        content.setAttribute('aria-current', 'page');
      }
      item.append(content);
      fullList.append(item);
      if (index >= trail.length - visible) trailList.append(item.cloneNode(true));
    });
    if (hasPrevious) {
      const previous = trail.at(-2);
      const label = previous.type === 'search' ? host.dataset.results : previous.type === 'article' ? host.dataset.article : previous.type === 'product' ? host.dataset.product : host.dataset.return.replace('[page]', previous.title);
      back.href = previous.url;
      back.dataset.pathIndex = String(trail.length - 2);
      backLabel.textContent = label;
      back.title = previous.title;
    } else {
      back.href = fallbackHref;
      backLabel.textContent = fallbackLabel;
      delete back.dataset.pathIndex;
      back.removeAttribute('title');
    }
  }

  function restore(entry, focus = false) {
    cancelRestore();
    if (host.dataset.restore !== 'true' || !entry) return;
    const container = getScrollContainer();
    let stopped = false;
    let observer;
    let timer;
    let frame;
    const stop = () => {
      stopped = true;
      observer?.disconnect();
      clearTimeout(timer);
      cancelAnimationFrame(frame);
      for (const name of ['wheel', 'touchstart', 'pointerdown', 'keydown']) document.removeEventListener(name, stop, true);
      document.removeEventListener('load', schedule, true);
    };
    const apply = () => {
      if (stopped) return;
      const card = entry.anchor ? [...document.querySelectorAll('#MainContent [data-product-id][data-page]')].find((node) => node.dataset.productId === entry.anchor.product) : null;
      const target = card ? container.scrollTop + card.getBoundingClientRect().top - viewportTop(container) - entry.anchor.offset : entry.scroll;
      if (Number.isFinite(target)) container.scrollTo({ top: Math.max(0, target), behavior: 'instant' });
      if (focus && card) {
        card.querySelector('a[href]')?.focus({ preventScroll: true });
        focus = false;
      }
    };
    function schedule() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(apply);
    }
    for (const name of ['wheel', 'touchstart', 'pointerdown', 'keydown']) document.addEventListener(name, stop, { capture: true, passive: true });
    document.addEventListener('load', schedule, true);
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(schedule);
      observer.observe(document.getElementById('MainContent') || container);
    }
    timer = setTimeout(stop, 1800);
    cancelRestore = stop;
    schedule();
  }

  const saved = stored();
  const own = stateTrail();
  const current = currentEntry();
  const navigationType = performance.getEntriesByType('navigation')[0]?.type;
  let restoreEntry = null;
  let focusReturn = false;
  if (own.at(-1)?.key === current.key) {
    trail = own;
    if (navigationType === 'back_forward' || navigationType === 'reload') restoreEntry = structuredClone(own.at(-1));
    Object.assign(trail.at(-1), { title: current.title, url: current.url });
  } else if (saved?.intent && Date.now() - saved.intent.time < 120000 && pageKey(saved.intent.to) === current.key && (!document.referrer || pageKey(document.referrer) === saved.intent.from)) {
    const incoming = validTrail(saved.intent.trail);
    if (saved.intent.restore && incoming.at(-1)?.key === current.key) {
      trail = incoming;
      restoreEntry = structuredClone(trail.at(-1));
      focusReturn = saved.intent.focus;
      Object.assign(trail.at(-1), { title: current.title, url: current.url });
    } else {
      trail = visit(incoming, current);
    }
  } else if (saved?.trail?.at(-1)?.key === pageKey(document.referrer) && navigationType !== 'reload') {
    trail = visit(validTrail(saved.trail), current);
  } else {
    trail = [current];
  }
  trail = validTrail(trail);
  stamp();
  render();

  function updateAddress() {
    const next = currentEntry();
    if (!next.key) return;
    if (trail.at(-1)?.key === next.key) {
      Object.assign(trail.at(-1), { title: next.title, url: next.url });
    } else {
      trail = visit(trail, next);
    }
    if (intent && !intent.restore) intent.trail = structuredClone(trail);
    stamp();
  }

  history.replaceState = function (state, title, url) {
    if (writing) return nativeReplace(state, title, url);
    writing = true;
    try {
      nativeReplace(state, title, url);
      updateAddress();
      if (url !== undefined) renderedUrl = location.href;
    } finally {
      writing = false;
    }
  };

  history.pushState = function (state, title, url) {
    if (writing) return nativePush(state, title, url);
    remember();
    stamp();
    writing = true;
    try {
      nativePush(state, title, url);
      updateAddress();
      renderedUrl = location.href;
      render();
    } finally {
      writing = false;
    }
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const anchor = event.target.closest('a[href]');
    if (!anchor || anchor.hasAttribute('download') || !pageKey(anchor.href)) return;
    departureCard = anchor.closest('[data-product-id][data-page]');
    remember(anchor);
    stamp();
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (anchor.target && anchor.target !== '_self')) return;
    const destination = localURL(anchor.href);
    if (destination.pathname === location.pathname && destination.search === location.search && destination.hash) {
      cancelRestore();
      return;
    }
    const index = host.contains(anchor) && anchor.dataset.pathIndex !== undefined ? Number(anchor.dataset.pathIndex) : -1;
    const returning = index >= 0 && index < trail.length - 1;
    intent = { time: Date.now(), from: trail.at(-1)?.key, to: cleanURL(anchor.href), restore: returning, focus: event.detail === 0, trail: structuredClone(returning ? trail.slice(0, index + 1) : trail) };
    persist();
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.method.toLowerCase() !== 'get') return;
    const destination = localURL(form.action);
    if (!destination || !pageKey(destination.href)) return;
    for (const [key, value] of new FormData(form)) if (typeof value === 'string') destination.searchParams.set(key, value);
    remember();
    stamp();
    intent = { time: Date.now(), from: trail.at(-1)?.key, to: cleanURL(destination.href), restore: false, trail: structuredClone(trail) };
    persist();
  }, true);

  window.addEventListener('pagehide', () => {
    remember(departureCard);
    if (intent && !intent.restore) intent.trail = structuredClone(trail);
    stamp();
    cancelRestore();
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    const restored = stateTrail();
    if (restored.at(-1)?.key !== pageKey(location.href)) return;
    trail = restored;
    departureCard = null;
    intent = null;
    stamp();
    render();
    restore(structuredClone(trail.at(-1)));
  });

  window.addEventListener('popstate', async () => {
    const version = ++renderVersion;
    cancelRestore();
    const restored = stateTrail();
    const next = currentEntry();
    const previousURL = localURL(renderedUrl);
    const currentURL = localURL(location.href);
    const fragmentOnly = previousURL?.pathname === currentURL.pathname && previousURL?.search === currentURL.search && previousURL?.hash !== currentURL.hash;
    if (fragmentOnly) {
      const previousEntry = restored.at(-1)?.key === next.key ? restored.at(-1) : null;
      if (previousEntry) trail = restored;
      intent = null;
      renderedUrl = location.href;
      updateAddress();
      render();
      if (!currentURL.hash && previousEntry) restore(structuredClone(previousEntry));
      return;
    }
    trail = restored.at(-1)?.key === next.key ? restored : [next];
    intent = null;
    const target = structuredClone(trail.at(-1));
    const needsList = ['collection', 'search'].includes(next.type) && renderedUrl !== location.href;
    renderedUrl = location.href;
    stamp();
    render();
    if (needsList) {
      const list = document.querySelector('results-list[section-id]');
      if (list) {
        try {
          await sectionRenderer.renderSection(list.getAttribute('section-id'), { url: new URL(location.href) });
        } catch {
          if (version === renderVersion) location.reload();
          return;
        }
      }
    }
    if (version === renderVersion) restore(target);
  });

  document.addEventListener('click', (event) => {
    if (details.open && !details.contains(event.target)) details.open = false;
  });
  details.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && details.open) {
      event.preventDefault();
      event.stopPropagation();
      details.open = false;
      details.querySelector('summary').focus();
    }
  });
  if (restoreEntry) {
    if (document.readyState === 'complete') restore(restoreEntry, focusReturn);
    else window.addEventListener('pageshow', () => restore(restoreEntry, focusReturn), { once: true });
  }
}
